import { submitCalls, cloudCalls, listCalls, seatsCalls, listFields } from './gate';
import type { PlanRules } from './plan/planTypes';

export const OWNER_FIELD = 'ownerId';

export interface PublishedSchema {
  name: string;
  fields: { name: string; type: 'string' }[];
  webBookable?: boolean;
  ownership?: 'user' | 'ownerWrite';
  ownerField?: string;
  subscriberList?: boolean;
  /** Server-enforced limits for this collection — capacity, deadline, one-per-person,
   *  waitlist, public counts. Enforced in crud.ts; see infra/lambda/shared/appRules.ts. */
  appRules?: PlanRules;
}


/**
 * What an app's own code says its collections are.
 *
 * Pure analysis of a source string, and it lived in `saddleHost.ts` — the client publish
 * orchestration, which imports the project store. So the one thing the SERVER needed in
 * order to derive an app's schemas from the document it already receives was locked
 * inside the client, and the gate it depends on had to ship in the binary to keep it
 * company. Same shape as the date token: a pure function trapped in a place that could
 * not travel.
 */

/**
 * Every collection an app needs declared server-side, and the access rule each one gets.
 *
 * Three kinds, and the rule is the difference between them:
 *  - `S.submit`    → webBookable: anyone may add, nobody may read. A waitlist.
 *  - `S.cloud`     → ownership 'user': the server stamps the owner and filters list reads
 *                    to the caller's own rows, so one person's data is theirs alone.
 *
 * `S.subscribe` needs nothing here: it posts to the platform's own double opt-in route,
 * which owns the subscriber list for the whole app rather than a per-entity collection.
 */
export function publishedSchemas(source: string): PublishedSchema[] {
  const out: PublishedSchema[] = submitSchemas(source);

  for (const call of cloudCalls(source)) {
    out.push({
      name: call.entity,
      fields: [...call.fields, OWNER_FIELD].map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'user',
      ownerField: OWNER_FIELD,
    });
  }

  // `S.list` collections are the owner's content that visitors read — a rota, a timetable.
  // The fields are the ones the APP reads, recorded so the owner is offered the right
  // columns rather than having to guess: an app reading `session.limit` while the owner
  // types `places` shows "0 places left" forever, with nothing saying why. They are
  // advisory — `ownerWrite` accepts columns the owner adds later, which is the point of it.
  const expected = listFields(source);
  for (const name of listCalls(source)) {
    if (out.some((sc) => sc.name === name)) continue;
    out.push({
      name,
      fields: (expected[name] ?? []).map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'ownerWrite',
    });
  }

  return out;
}

/**
 * Attaches the plan's server-enforced rules to the collection the app submits into.
 *
 * This is the step that was missing: the enforcement in crud.ts has been written and
 * tested since the appRules work, and nothing ever declared a rule, so no published app
 * has ever had a capacity that held. A limit only exists once it is on the schema.
 */
export function withAppRules(schemas: PublishedSchema[], rules: PlanRules | null | undefined): PublishedSchema[] {
  if (!rules || !Object.keys(rules).length) return schemas;
  // Only the write-only collection takes them: per-user cloud rows and the subscriber
  // list are not the thing being rationed.
  const target = schemas.find((sc) => sc.webBookable);
  if (!target) return schemas;
  return schemas.map((sc) => (sc === target ? { ...sc, appRules: rules } : sc));
}

/**
 * An app that shows "4 spots left" needs the counts endpoint open, and the counts
 * endpoint only exists when the collection declares `countBy`.
 *
 * Derived from the app's own code rather than trusted from the plan: the page is what
 * decides whether it displays availability, and a plan whose capacity was grouped by one
 * field while the app asks about another would leave `S.seats` reading an endpoint that
 * returns nothing. The grouping follows the capacity rule, which is what the count is
 * about; failing that, the field the app groups its submissions by.
 */
/**
 * Declare the owner's collections the PLAN promised, whether or not the app used them.
 *
 * `publishedSchemas` reads the app's own `S.list` calls, which is right — but a build that
 * reached for `S.store` instead leaves the owner with nothing to edit and no sign of why.
 * Declaring them anyway means the Live tab still offers the right columns, and a re-publish
 * of a corrected app finds its collection already there with its rows intact.
 */
export function withManaged(
  schemas: PublishedSchema[],
  manages: { collection: string; fields: string[] }[] | undefined,
): PublishedSchema[] {
  const out = [...schemas];
  for (const m of manages ?? []) {
    if (out.some((sc) => sc.name === m.collection)) continue;
    out.push({
      name: m.collection,
      fields: m.fields.map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'ownerWrite',
    });
  }
  return out;
}

export function withCounts(schemas: PublishedSchema[], source: string): PublishedSchema[] {
  const wanted = seatsCalls(source);
  if (!wanted.length) return schemas;

  return schemas.map((sc) => {
    if (!wanted.includes(sc.name)) return sc;
    const rules = sc.appRules ?? {};
    if (rules.countBy) return sc;
    const groupBy = rules.capacity?.groupBy;
    // No grouping anywhere means one bucket for the whole collection, which the server
    // reports under a single key — still useful for "12 people have signed up".
    return { ...sc, appRules: { ...rules, countBy: groupBy ?? sc.fields[0]?.name ?? 'id' } };
  });
}

export function submitSchemas(source: string): { name: string; fields: { name: string; type: 'string' }[]; webBookable: true }[] {
  const byEntity = new Map<string, Set<string>>();
  for (const call of submitCalls(source)) {
    const fields = byEntity.get(call.entity) ?? new Set<string>();
    for (const f of call.fields ?? []) fields.add(f);
    byEntity.set(call.entity, fields);
  }
  return [...byEntity].map(([name, fields]) => ({
    name,
    fields: [...fields].map((f) => ({ name: f, type: 'string' as const })),
    webBookable: true as const,
  }));
}

